import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

export const HeaderSearchBox = ({
  label = 'NAME',
  value = '',
  onSearch,
  placeholder = 'Search...',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  // Sync with external value
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    // Focus input when opened
    setTimeout(() => inputRef.current?.focus(), 50);

    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => updatePosition();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updatePosition]);

  const handleSubmit = () => {
    onSearch(inputValue.trim());
    setIsOpen(false);
  };

  const handleClear = () => {
    setInputValue('');
    onSearch('');
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  const hasValue = value && value.trim().length > 0;

  const dropdown = isOpen
    ? createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            transform: 'translateX(-50%)',
            zIndex: 9999,
          }}
          className="bg-jp-section border border-jp-line rounded-lg shadow-xl p-2 min-w-[180px]"
        >
          <div className="flex items-center gap-1.5">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="flex-1 px-2 py-1.5 text-[11px] rounded bg-jp-section border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500"
            />
            {inputValue.trim() && (
              <button
                onClick={handleSubmit}
                className="p-1.5 bg-cyan-600 hover:bg-cyan-500 rounded transition-colors flex-shrink-0"
                title="Search"
              >
                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>
            )}
            {hasValue && (
              <button
                onClick={handleClear}
                className="p-1.5 bg-jp-section hover:bg-jp-section rounded transition-colors flex-shrink-0"
                title="Clear"
              >
                <svg className="w-3 h-3 text-jp-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 mx-auto cursor-pointer hover:text-jp-accent-ink transition-colors"
      >
        <span>{label}</span>
        {hasValue && (
          <span className="bg-cyan-500 text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
        )}
        <svg className="w-3 h-3 ml-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </button>
      {dropdown}
    </>
  );
};

export default HeaderSearchBox;
